/**
 * Schedules Management Page
 *
 * CRUD for provider weekly schedules and date-specific overrides (ADMIN only)
 * Two tabs: weekly schedules + schedule exceptions
 */

import { useState, useMemo, useEffect } from 'react'
import {
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  Clock,
  CalendarOff,
  Calendar,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { MultiSelect } from '@/presentation/components/ui/MultiSelect'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/presentation/components/ui/Tabs'
import { SemanaDeAgenda } from '@/presentation/components/agenda/SemanaDeAgenda'
import { DeclararLaSemana } from '@/presentation/components/agenda/DeclararLaSemana'
import {
  useSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
  useOverrides,
  useCreateOverride,
  useDeleteOverride,
} from '@/application/appointments'
import { useRadiologists } from '@/application/users/useUsers'
import { useActiveModalities } from '@/application/modalities'
import { useUserSubscriptions } from '@/application/modality-subscriptions'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import {
  DAY_NAMES,
  type ProviderSchedule,
  type ScheduleOverride,
} from '@/domain/appointment/types'

// =============================================================================
// Constants
// =============================================================================


const DAY_FORM_OPTIONS = [
  { value: '1', label: 'Lunes' },
  { value: '2', label: 'Martes' },
  { value: '3', label: 'Miercoles' },
  { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' },
  { value: '6', label: 'Sabado' },
  { value: '0', label: 'Domingo' },
]

const OVERRIDE_TYPE_OPTIONS = [
  { value: 'block', label: 'Bloquear dia' },
  { value: 'extra', label: 'Horas extra' },
]

function getFirstDayOfMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

function getLastDayOfMonth(): string {
  const now = new Date()
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`
}

// =============================================================================
// Schedule Form Modal
// =============================================================================

interface ScheduleFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedule?: ProviderSchedule | null
  onSuccess?: () => void
  providerOptions: Array<{ value: string; label: string }>
  modalityOptions: Array<{ value: string; label: string }>
}

function ScheduleFormModal({
  open,
  onOpenChange,
  schedule,
  onSuccess,
  providerOptions,
  modalityOptions,
}: ScheduleFormModalProps) {
  const isEditing = !!schedule

  const [providerId, setProviderId] = useState(schedule?.providerId || '')
  const [modalityIds, setModalityIds] = useState<string[]>(schedule?.modalityId ? [schedule.modalityId] : [])
  const [dayOfWeek, setDayOfWeek] = useState(schedule?.dayOfWeek?.toString() || '')

  // Filter modality options to only those the selected provider is subscribed to
  const { data: providerSubscriptions } = useUserSubscriptions(providerId)

  const filteredModalityOptions = useMemo(() => {
    if (!providerId || isEditing) return modalityOptions
    if (!providerSubscriptions?.subscriptions) return []
    const subscribedIds = new Set(providerSubscriptions.subscriptions.map((s) => s.modalityId))
    return modalityOptions.filter((o) => subscribedIds.has(o.value))
  }, [providerId, providerSubscriptions, modalityOptions, isEditing])
  const [startTime, setStartTime] = useState(schedule?.startTime || '')
  const [endTime, setEndTime] = useState(schedule?.endTime || '')
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(
    schedule?.slotDurationMinutes?.toString() || ''
  )
  const [maxConcurrent, setMaxConcurrent] = useState(
    schedule?.maxConcurrent?.toString() || '1'
  )
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateSchedule()
  const updateMutation = useUpdateSchedule()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!providerId) {
      setError('El profesional es requerido')
      return
    }
    if (modalityIds.length === 0) {
      setError('Seleccione al menos una modalidad')
      return
    }
    if (dayOfWeek === '') {
      setError('El dia de la semana es requerido')
      return
    }
    if (!startTime) {
      setError('La hora de inicio es requerida')
      return
    }
    if (!endTime) {
      setError('La hora de fin es requerida')
      return
    }
    if (startTime >= endTime) {
      setError('La hora de inicio debe ser anterior a la hora de fin')
      return
    }
    if (!slotDurationMinutes) {
      setError('La duracion del turno es requerida')
      return
    }

    try {
      if (isEditing && schedule) {
        await updateMutation.mutateAsync({
          id: schedule.id,
          data: {
            startTime,
            endTime,
            slotDurationMinutes: parseInt(slotDurationMinutes, 10),
            maxConcurrent: maxConcurrent ? parseInt(maxConcurrent, 10) : undefined,
          },
        })
        toast.success('Horario actualizado correctamente')
      } else {
        // Create one schedule per modality
        for (const modalityId of modalityIds) {
          await createMutation.mutateAsync({
            providerId,
            modalityId,
            dayOfWeek: parseInt(dayOfWeek, 10),
            startTime,
            endTime,
            slotDurationMinutes: parseInt(slotDurationMinutes, 10),
            maxConcurrent: maxConcurrent ? parseInt(maxConcurrent, 10) : undefined,
          })
        }
        toast.success(
          modalityIds.length > 1
            ? `${modalityIds.length} horarios creados correctamente`
            : 'Horario creado correctamente'
        )
      }

      handleClose()
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, 'Error al guardar el horario')
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setProviderId('')
    setModalityIds([])
    setDayOfWeek('')
    setStartTime('')
    setEndTime('')
    setSlotDurationMinutes('')
    setMaxConcurrent('1')
    setError(null)
    onOpenChange(false)
  }

  // Reset form when schedule changes or modal opens
  useEffect(() => {
    if (open && schedule) {
      setProviderId(schedule.providerId)
      setModalityIds([schedule.modalityId])
      setDayOfWeek(schedule.dayOfWeek.toString())
      setStartTime(schedule.startTime)
      setEndTime(schedule.endTime)
      setSlotDurationMinutes(schedule.slotDurationMinutes.toString())
      setMaxConcurrent(schedule.maxConcurrent.toString())
    } else if (open && !schedule) {
      setProviderId('')
      setModalityIds([])
      setDayOfWeek('')
      setStartTime('')
      setEndTime('')
      setSlotDurationMinutes('')
      setMaxConcurrent('1')
    }
    setError(null)
  }, [open, schedule])

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Horario' : 'Nuevo Horario'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifique los datos del horario semanal'
              : 'Complete los datos para crear un nuevo horario semanal'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Profesional <span className="text-red-500">*</span>
            </label>
            <Select
              options={providerOptions}
              value={providerId}
              onChange={(e) => {
                setProviderId(e.target.value)
                if (!isEditing) setModalityIds([])
              }}
              placeholder="Seleccione un profesional"
              disabled={isEditing}
            />
            {isEditing && (
              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                El profesional no se puede cambiar
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              {isEditing ? 'Modalidad' : 'Modalidades'} <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <>
                <Select
                  options={modalityOptions}
                  value={modalityIds[0] || ''}
                  onChange={() => {}}
                  placeholder="Seleccione una modalidad"
                  disabled
                />
                <p className="text-xs text-[var(--foreground-muted)] mt-1">
                  La modalidad no se puede cambiar
                </p>
              </>
            ) : (
              <>
                <MultiSelect
                  options={filteredModalityOptions}
                  value={modalityIds}
                  onChange={setModalityIds}
                  placeholder={providerId ? 'Seleccione modalidades' : 'Primero seleccione un profesional'}
                  disabled={!providerId}
                />
                {providerId && filteredModalityOptions.length === 0 && providerSubscriptions && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    Este profesional no tiene modalidades asignadas. Asigne modalidades desde la seccion de suscripciones.
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Dia de la semana <span className="text-red-500">*</span>
            </label>
            <Select
              options={DAY_FORM_OPTIONS}
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              placeholder="Seleccione un dia"
              disabled={isEditing}
            />
            {isEditing && (
              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                El dia no se puede cambiar
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Hora inicio <span className="text-red-500">*</span>
              </label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Hora fin <span className="text-red-500">*</span>
              </label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Duracion del turno (min) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                value={slotDurationMinutes}
                onChange={(e) => setSlotDurationMinutes(e.target.value)}
                placeholder="30"
                min={5}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Concurrencia maxima
              </label>
              <Input
                type="number"
                value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
                placeholder="1"
                min={1}
              />
              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                Turnos simultaneos permitidos
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isEditing ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  {isEditing ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Crear
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Schedule Delete Confirmation Modal
// =============================================================================

interface DeleteScheduleModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedule: ProviderSchedule | null
  providerName: string
  modalityName: string
  onConfirm: () => void
  isPending: boolean
}

function DeleteScheduleModal({
  open,
  onOpenChange,
  schedule,
  providerName,
  modalityName,
  onConfirm,
  isPending,
}: DeleteScheduleModalProps) {
  const dayName = schedule ? DAY_NAMES[schedule.dayOfWeek] : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Eliminar Horario</DialogTitle>
          <DialogDescription>
            ¿Esta seguro que desea eliminar el horario de <strong>{providerName}</strong> para{' '}
            <strong>{modalityName}</strong> los dias <strong>{dayName}</strong>?
            Esta accion no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400 mt-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">
            Los turnos ya programados con este horario no seran afectados.
          </span>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Override Form Modal
// =============================================================================

interface OverrideFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultProviderId?: string
  providerOptions: Array<{ value: string; label: string }>
  onSuccess?: () => void
}

function OverrideFormModal({
  open,
  onOpenChange,
  defaultProviderId,
  providerOptions,
  onSuccess,
}: OverrideFormModalProps) {
  const [providerId, setProviderId] = useState(defaultProviderId || '')
  const [overrideDate, setOverrideDate] = useState('')
  const [overrideType, setOverrideType] = useState('block')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateOverride()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!providerId) {
      setError('El profesional es requerido')
      return
    }
    if (!overrideDate) {
      setError('La fecha es requerida')
      return
    }
    if (overrideType === 'extra') {
      if (!startTime) {
        setError('La hora de inicio es requerida para horas extra')
        return
      }
      if (!endTime) {
        setError('La hora de fin es requerida para horas extra')
        return
      }
      if (startTime >= endTime) {
        setError('La hora de inicio debe ser anterior a la hora de fin')
        return
      }
    }

    try {
      await createMutation.mutateAsync({
        providerId,
        overrideDate,
        isAvailable: overrideType === 'extra',
        startTime: overrideType === 'extra' ? startTime : undefined,
        endTime: overrideType === 'extra' ? endTime : undefined,
        reason: reason.trim() || undefined,
      })
      toast.success('Excepcion creada correctamente')
      handleClose()
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, 'Error al crear la excepcion')
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setProviderId(defaultProviderId || '')
    setOverrideDate('')
    setOverrideType('block')
    setStartTime('')
    setEndTime('')
    setReason('')
    setError(null)
    onOpenChange(false)
  }

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setProviderId(defaultProviderId || '')
      setOverrideDate('')
      setOverrideType('block')
      setStartTime('')
      setEndTime('')
      setReason('')
      setError(null)
    }
  }, [open, defaultProviderId])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>Nueva Excepcion</DialogTitle>
          <DialogDescription>
            Agregue una excepcion al horario habitual del profesional
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Profesional <span className="text-red-500">*</span>
            </label>
            <Select
              options={providerOptions}
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              placeholder="Seleccione un profesional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Fecha <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Tipo <span className="text-red-500">*</span>
            </label>
            <Select
              options={OVERRIDE_TYPE_OPTIONS}
              value={overrideType}
              onChange={(e) => setOverrideType(e.target.value)}
            />
          </div>

          {overrideType === 'extra' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                  Hora inicio <span className="text-red-500">*</span>
                </label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                  Hora fin <span className="text-red-500">*</span>
                </label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Motivo
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Vacaciones, congreso, etc."
            />
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Override Delete Confirmation Modal
// =============================================================================

interface DeleteOverrideModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  override: ScheduleOverride | null
  onConfirm: () => void
  isPending: boolean
}

function DeleteOverrideModal({
  open,
  onOpenChange,
  override,
  onConfirm,
  isPending,
}: DeleteOverrideModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Eliminar Excepcion</DialogTitle>
          <DialogDescription>
            ¿Esta seguro que desea eliminar la excepcion del dia{' '}
            <strong>{override?.overrideDate}</strong>?
            Esta accion no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function SchedulesPage() {
  const [activeTab, setActiveTab] = useState('schedules')

  // ---- Schedule state ----
  const [providerFilter, setProviderFilter] = useState('')
  const [modalityFilter, setModalityFilter] = useState('')
  const [showCreateScheduleModal, setShowCreateScheduleModal] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<ProviderSchedule | null>(null)
  const [deletingSchedule, setDeletingSchedule] = useState<ProviderSchedule | null>(null)
  /**
   * Lo que el usuario pidio declarar: que dias y, si vino de pulsar un hueco,
   * a que hora. Nulo significa que el dialogo esta cerrado.
   */
  const [semanaPedida, setSemanaPedida] = useState<{ dias: number[]; hora?: string } | null>(
    null
  )

  // ---- Override state ----
  const [overrideProviderFilter, setOverrideProviderFilter] = useState('')
  const [overrideDateFrom, setOverrideDateFrom] = useState(getFirstDayOfMonth)
  const [overrideDateTo, setOverrideDateTo] = useState(getLastDayOfMonth)
  const [showCreateOverrideModal, setShowCreateOverrideModal] = useState(false)
  const [deletingOverride, setDeletingOverride] = useState<ScheduleOverride | null>(null)

  // ---- Shared queries ----
  const { data: radiologistsData } = useRadiologists()
  const { data: modalitiesData } = useActiveModalities()

  // ---- Schedule queries ----
  const { data: schedulesData, isLoading: schedulesLoading } = useSchedules({
    providerId: providerFilter || undefined,
    modalityId: modalityFilter || undefined,
  })

  const deleteScheduleMutation = useDeleteSchedule()

  // ---- Override queries ----
  const { data: overridesData, isLoading: overridesLoading } = useOverrides(
    overrideProviderFilter || undefined,
    overrideDateFrom,
    overrideDateTo
  )

  const deleteOverrideMutation = useDeleteOverride()

  // ---- Lookup maps ----
  const providerMap = useMemo(() => {
    const map = new Map<string, string>()
    if (radiologistsData) radiologistsData.forEach((r) => map.set(r.id, r.fullName))
    return map
  }, [radiologistsData])

  const modalityMap = useMemo(() => {
    const map = new Map<string, string>()
    if (modalitiesData?.modalities) {
      modalitiesData.modalities.forEach((m) => map.set(m.id, `${m.name} (${m.code})`))
    }
    return map
  }, [modalitiesData?.modalities])

  // ---- Select options ----
  const providerOptions = useMemo(() => {
    if (!radiologistsData) return []
    return radiologistsData.map((r) => ({
      value: r.id,
      label: r.fullName,
    }))
  }, [radiologistsData])

  const modalityOptions = useMemo(() => {
    if (!modalitiesData?.modalities) return []
    return modalitiesData.modalities.map((m) => ({
      value: m.id,
      label: `${m.name} (${m.code})`,
    }))
  }, [modalitiesData?.modalities])

  const modalityFilterOptions = useMemo(() => {
    return [{ value: '', label: 'Todas las modalidades' }, ...modalityOptions]
  }, [modalityOptions])

  // ---- Filtered schedules ----
  const filteredSchedules = useMemo(() => {
    if (!schedulesData?.schedules) return []
    return [...schedulesData.schedules]
  }, [schedulesData?.schedules])

  // ---- Filtered overrides ----
  const filteredOverrides = useMemo(() => {
    if (!overridesData?.overrides) return []
    return [...overridesData.overrides]
  }, [overridesData?.overrides])

  // ---- Handlers ----
  const handleDeleteSchedule = () => {
    if (deletingSchedule) {
      deleteScheduleMutation.mutate(deletingSchedule.id, {
        onSuccess: () => {
          toast.success('Horario eliminado correctamente')
          setDeletingSchedule(null)
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al eliminar el horario'))
        },
      })
    }
  }

  const handleDeleteOverride = () => {
    if (deletingOverride) {
      deleteOverrideMutation.mutate(deletingOverride.id, {
        onSuccess: () => {
          toast.success('Excepcion eliminada correctamente')
          setDeletingOverride(null)
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al eliminar la excepcion'))
        },
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Horarios</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestion de horarios semanales y excepciones de los profesionales
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="schedules">
            <Clock className="h-4 w-4 mr-2" />
            Horarios Semanales
          </TabsTrigger>
          <TabsTrigger value="overrides">
            <CalendarOff className="h-4 w-4 mr-2" />
            Excepciones
          </TabsTrigger>
        </TabsList>

        {/* ================================================================= */}
        {/* Tab 1: Weekly Schedules                                            */}
        {/* ================================================================= */}
        <TabsContent value="schedules">
          <div className="space-y-6">
            {/* Quien y para que.
                El profesional dejo de ser un filtro y paso a ser la pregunta:
                un calendario con varios profesionales encima es ilegible, y
                lo que uno quiere saber es "como queda la semana de Rocio". */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-[240px]">
                    <label className="text-sm" htmlFor="agenda-profesional">
                      Profesional
                    </label>
                    <Select
                      id="agenda-profesional"
                      options={[
                        { value: '', label: 'Elija un profesional' },
                        ...providerOptions,
                      ]}
                      value={providerFilter}
                      onChange={(e) => setProviderFilter(e.target.value)}
                    />
                  </div>

                  <div className="w-[240px]">
                    <label className="text-sm" htmlFor="agenda-modalidad">
                      Modalidad
                    </label>
                    <Select
                      id="agenda-modalidad"
                      options={modalityFilterOptions}
                      value={modalityFilter}
                      onChange={(e) => setModalityFilter(e.target.value)}
                    />
                  </div>

                  <Button
                    className="ml-auto"
                    disabled={!providerFilter}
                    onClick={() => setSemanaPedida({ dias: [1, 2, 3, 4, 5] })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Declarar la semana
                  </Button>
                </div>
              </CardContent>
            </Card>

            {!providerFilter ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                  <p className="mt-3 text-lg">Elija un profesional para ver su semana</p>
                  <p className="mt-1 text-sm text-[var(--foreground-muted)]">
                    Se muestra de uno en uno: varias agendas sobre la misma rejilla no se
                    pueden leer.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  {schedulesLoading ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
                    </div>
                  ) : (
                    <>
                      <SemanaDeAgenda
                        franjas={filteredSchedules.map((f) => ({
                          id: f.id,
                          dayOfWeek: f.dayOfWeek,
                          startTime: f.startTime,
                          endTime: f.endTime,
                          slotDurationMinutes: f.slotDurationMinutes,
                          maxConcurrent: f.maxConcurrent,
                          etiqueta: modalityMap.get(f.modalityId),
                          isActive: f.isActive,
                        }))}
                        onAbrirFranja={(franja) => {
                          const original = filteredSchedules.find((f) => f.id === franja.id)
                          if (original) setEditingSchedule(original)
                        }}
                        onHueco={(dia, hora) => setSemanaPedida({ dias: [dia], hora })}
                      />

                      {filteredSchedules.length === 0 && (
                        <p className="mt-4 text-center text-sm text-[var(--foreground-muted)]">
                          Esta agenda está vacía. Pulse sobre una hora para empezar, o use
                          <strong> Declarar la semana</strong>.
                        </p>
                      )}

                      <p className="mt-4 text-sm text-[var(--foreground-muted)]">
                        Pulse una franja para editarla, o un hueco para agregar atención a esa
                        hora.
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ================================================================= */}
        {/* Tab 2: Overrides                                                   */}
        {/* ================================================================= */}
        <TabsContent value="overrides">
          <div className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="w-[220px]">
                    <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                      Profesional
                    </label>
                    <Select
                      options={providerOptions}
                      value={overrideProviderFilter}
                      onChange={(e) => setOverrideProviderFilter(e.target.value)}
                      placeholder="Seleccione un profesional"
                    />
                  </div>

                  <div className="w-[170px]">
                    <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                      Desde
                    </label>
                    <Input
                      type="date"
                      value={overrideDateFrom}
                      onChange={(e) => setOverrideDateFrom(e.target.value)}
                    />
                  </div>

                  <div className="w-[170px]">
                    <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                      Hasta
                    </label>
                    <Input
                      type="date"
                      value={overrideDateTo}
                      onChange={(e) => setOverrideDateTo(e.target.value)}
                    />
                  </div>

                  <div className="text-sm text-[var(--foreground-muted)] ml-auto">
                    {filteredOverrides.length} excepciones
                  </div>

                  <Button onClick={() => setShowCreateOverrideModal(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Nueva Excepcion
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                {!overrideProviderFilter ? (
                  <div className="flex flex-col items-center justify-center p-8">
                    <Calendar className="h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                    <p className="mt-2 text-[var(--foreground-muted)]">
                      Seleccione un profesional para ver sus excepciones
                    </p>
                  </div>
                ) : overridesLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Disponible</TableHead>
                        <TableHead>Horario</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead className="w-[80px]">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOverrides.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8">
                            <CalendarOff className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                            <p className="mt-2 text-[var(--foreground-muted)]">
                              No hay excepciones en este periodo
                            </p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredOverrides.map((override) => (
                          <TableRow key={override.id}>
                            <TableCell className="font-medium">
                              {override.overrideDate}
                            </TableCell>
                            <TableCell>
                              <Badge variant={override.isAvailable ? 'success' : 'destructive'}>
                                {override.isAvailable ? 'Si' : 'No'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {override.isAvailable && override.startTime && override.endTime
                                ? `${override.startTime} - ${override.endTime}`
                                : 'Bloqueado'}
                            </TableCell>
                            <TableCell className="text-[var(--foreground-muted)]">
                              {override.reason || '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeletingOverride(override)}
                                title="Eliminar"
                                className="text-[var(--destructive)] hover:text-[var(--destructive)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ================================================================= */}
      {/* Modals                                                             */}
      {/* ================================================================= */}

      {/* Declarar la semana: varios dias de una vez. El modal de abajo sigue
          existiendo para EDITAR una franja concreta, que es otra cosa. */}
      {semanaPedida && providerFilter && (
        <DeclararLaSemana
          abierto
          onCerrar={() => setSemanaPedida(null)}
          onCreada={() => setSemanaPedida(null)}
          providerId={providerFilter}
          nombreDelProfesional={providerMap.get(providerFilter) ?? 'el profesional'}
          modalidades={modalityOptions}
          modalidadPorDefecto={modalityFilter || undefined}
          diasIniciales={semanaPedida.dias}
          horaInicial={semanaPedida.hora}
        />
      )}

      {/* Schedule Create/Edit Modal */}
      <ScheduleFormModal
        open={showCreateScheduleModal || !!editingSchedule}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateScheduleModal(false)
            setEditingSchedule(null)
          }
        }}
        schedule={editingSchedule}
        providerOptions={providerOptions}
        modalityOptions={modalityOptions}
      />

      {/* Schedule Delete Confirmation Modal */}
      <DeleteScheduleModal
        open={!!deletingSchedule}
        onOpenChange={(open) => !open && setDeletingSchedule(null)}
        schedule={deletingSchedule}
        providerName={deletingSchedule ? (providerMap.get(deletingSchedule.providerId) || '') : ''}
        modalityName={deletingSchedule ? (modalityMap.get(deletingSchedule.modalityId) || '') : ''}
        onConfirm={handleDeleteSchedule}
        isPending={deleteScheduleMutation.isPending}
      />

      {/* Override Create Modal */}
      <OverrideFormModal
        open={showCreateOverrideModal}
        onOpenChange={setShowCreateOverrideModal}
        defaultProviderId={overrideProviderFilter}
        providerOptions={providerOptions}
      />

      {/* Override Delete Confirmation Modal */}
      <DeleteOverrideModal
        open={!!deletingOverride}
        onOpenChange={(open) => !open && setDeletingOverride(null)}
        override={deletingOverride}
        onConfirm={handleDeleteOverride}
        isPending={deleteOverrideMutation.isPending}
      />
    </div>
  )
}
